/**
 * Tiny timezone-safe helpers for ISO calendar dates (`yyyy-MM-dd`).
 * Every report lib works on DB date strings; doing the arithmetic in UTC keeps
 * «days between» stable regardless of the browser's timezone or DST.
 */

/** Parse `yyyy-MM-dd` (or a longer ISO string — only the date part is used) to a UTC timestamp. */
export function isoToUtcMs(iso: string): number {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  return Date.UTC(y, m - 1, d);
}

/** Format a UTC timestamp (or Date) as `yyyy-MM-dd`. */
export function utcMsToIso(ms: number | Date): string {
  return new Date(typeof ms === "number" ? ms : ms.getTime()).toISOString().slice(0, 10);
}

/** Add (or subtract) calendar days to an ISO date. */
export function addDaysIso(iso: string, days: number): string {
  return utcMsToIso(isoToUtcMs(iso) + days * 86_400_000);
}

/** Signed whole calendar days from `a` to `b` (positive when `b` is later). */
export function diffDaysIso(a: string, b: string): number {
  return Math.round((isoToUtcMs(b) - isoToUtcMs(a)) / 86_400_000);
}

/** The date part of a timestamp/Date as `yyyy-MM-dd` (local calendar day for Date inputs). */
export function toIsoDate(value: string | Date): string {
  if (typeof value === "string") return value.slice(0, 10);
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, "0");
  const d = String(value.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Today's local calendar date as `yyyy-MM-dd`. */
export function todayIso(now: Date = new Date()): string {
  return toIsoDate(now);
}

/** Median of a numeric list (null when empty). */
export function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Percentile (0..100) with linear interpolation (null when empty). */
export function percentile(values: number[], p: number): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const pos = ((sorted.length - 1) * Math.min(100, Math.max(0, p))) / 100;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}
